import { resolveWorkspaceAssetUrl, getWorkspaceDomains } from '../lib/urls';
/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Chatbot Widget Studio State Store (Task 10.3.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export interface WidgetStarterQuestion {
  id: string;
  question: string;
  category?: string;
}

export interface WidgetLeadCaptureConfig {
  enabled: boolean;
  title: string;
  subtitle: string;
  requireName: boolean;
  requireEmail: boolean;
  requirePhone: boolean;
  submitButtonText: string;
}

export interface WidgetAppearanceConfig {
  botName: string;
  botSubtitle: string;
  primaryColor: string; // Hex color e.g. #6366f1
  greetingMessage: string;
  launcherText: string;
  position: 'bottom-right' | 'bottom-left';
  avatarUrl: string;
  themeMode: 'light' | 'dark' | 'auto';
  autoOpenDelaySeconds: number; // 0 = disabled
}

export interface WidgetConfigState {
  botId: string;
  workspaceId: string;
  appearance: WidgetAppearanceConfig;
  starterQuestions: WidgetStarterQuestion[];
  leadCapture: WidgetLeadCaptureConfig;
  whitelistedDomains: string[];
  associatedKnowledgeBase: 'all' | 'specific' | 'none';
  selectedDocIds: string[];
}

export interface WidgetChatMessage {
  id: string;
  role: 'bot' | 'user';
  content: string;
  timestamp: string;
}

export interface WidgetStudioState {
  config: WidgetConfigState;
  activeTab: 'appearance' | 'behavior' | 'leads' | 'security' | 'embed';
  previewDevice: 'desktop' | 'mobile';
  previewWidgetOpen: boolean;
  testMessages: WidgetChatMessage[];
  isSimulatingResponse: boolean;
  hasUnsavedChanges: boolean;
  isSaving: boolean;

  // Actions
  setActiveTab: (tab: WidgetStudioState['activeTab']) => void;
  setPreviewDevice: (device: 'desktop' | 'mobile') => void;
  setPreviewWidgetOpen: (open: boolean) => void;
  togglePreviewWidget: () => void;

  // Config Mutators
  updateAppearance: (partial: Partial<WidgetAppearanceConfig>) => void;
  updateLeadCapture: (partial: Partial<WidgetLeadCaptureConfig>) => void;
  addStarterQuestion: (question: string) => void;
  removeStarterQuestion: (id: string) => void;
  updateStarterQuestion: (id: string, text: string) => void;
  addWhitelistedDomain: (domain: string) => void;
  removeWhitelistedDomain: (domain: string) => void;
  setAssociatedKnowledgeBase: (scope: 'all' | 'specific' | 'none') => void;

  // Interactive Live Chat in Preview
  sendTestMessage: (content: string) => Promise<void>;
  resetTestChat: () => void;

  // Persistence
  saveConfig: () => Promise<void>;
  resetConfigToDefault: () => void;
}

const STORAGE_KEY_WIDGET_CONFIG = 'sophia_chatbot_widget_config';

export const DEFAULT_WIDGET_CONFIG: WidgetConfigState = {
  botId: 'bot_enterprise_demo',
  workspaceId: 'ws_demo_primary',
  appearance: {
    botName: 'Sophia Assistant',
    botSubtitle: 'Trợ lý AI Doanh Nghiệp',
    primaryColor: '#6366f1', // Indigo Sophia Brand
    greetingMessage:
      'Xin chào! Tôi là trợ lý ảo AI Sophia được huấn luyện trực tiếp trên kho tri thức doanh nghiệp. Tôi có thể hỗ trợ gì cho bạn hôm nay?',
    launcherText: 'Chat với chúng tôi',
    position: 'bottom-right',
    avatarUrl: resolveWorkspaceAssetUrl('avatars/bot-avatar-01.png'),
    themeMode: 'light',
    autoOpenDelaySeconds: 0,
  },
  starterQuestions: [
    {
      id: 'q_01',
      question: 'Sophia Platform có ưu điểm gì so với kiến trúc cũ?',
      category: 'Kiến trúc',
    },
    {
      id: 'q_02',
      question: 'Thời gian trễ P95 của Cloudflare Workers và Vectorize là bao nhiêu?',
      category: 'Hiệu năng',
    },
    {
      id: 'q_03',
      question: 'Chính sách bảo mật dữ liệu và chuẩn mã hóa SOC2 như thế nào?',
      category: 'Bảo mật',
    },
  ],
  leadCapture: {
    enabled: true,
    title: 'Bắt đầu trò chuyện với Sophia',
    subtitle: 'Vui lòng cung cấp thông tin để chúng tôi hỗ trợ bạn tốt nhất:',
    requireName: true,
    requireEmail: true,
    requirePhone: false,
    submitButtonText: 'Bắt Đầu Hội Thoại',
  },
  whitelistedDomains: [
    getWorkspaceDomains().baseDomain,
    'fourelementscorp.com',
    'localhost:3000',
    'localhost:5173',
  ],
  associatedKnowledgeBase: 'all',
  selectedDocIds: [],
};

const INITIAL_TEST_MESSAGES: WidgetChatMessage[] = [
  {
    id: 'msg_welcome',
    role: 'bot',
    content:
      'Xin chào! Tôi là trợ lý ảo AI Sophia được huấn luyện trực tiếp trên kho tri thức doanh nghiệp. Tôi có thể hỗ trợ gì cho bạn hôm nay?',
    timestamp: 'Vừa xong',
  },
];

function loadStoredConfig(): WidgetConfigState {
  if (typeof window === 'undefined') return DEFAULT_WIDGET_CONFIG;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_WIDGET_CONFIG);
    if (!raw) return DEFAULT_WIDGET_CONFIG;
    return { ...DEFAULT_WIDGET_CONFIG, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_WIDGET_CONFIG;
  }
}

function saveStoredConfig(config: WidgetConfigState) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY_WIDGET_CONFIG, JSON.stringify(config));
  } catch (err) {
    console.error('Failed to save widget config to localStorage:', err);
  }
}

export const useWidgetStudioStore = create<WidgetStudioState>((set, get) => ({
  config: loadStoredConfig(),
  activeTab: 'appearance',
  previewDevice: 'desktop',
  previewWidgetOpen: true,
  testMessages: INITIAL_TEST_MESSAGES,
  isSimulatingResponse: false,
  hasUnsavedChanges: false,
  isSaving: false,

  setActiveTab: (activeTab) => set({ activeTab }),

  setPreviewDevice: (previewDevice) => set({ previewDevice }),

  setPreviewWidgetOpen: (previewWidgetOpen) => set({ previewWidgetOpen }),

  togglePreviewWidget: () => set((state) => ({ previewWidgetOpen: !state.previewWidgetOpen })),

  updateAppearance: (partial) => {
    set((state) => ({
      config: {
        ...state.config,
        appearance: {
          ...state.config.appearance,
          ...partial,
        },
      },
      hasUnsavedChanges: true,
    }));
  },

  updateLeadCapture: (partial) => {
    set((state) => ({
      config: {
        ...state.config,
        leadCapture: {
          ...state.config.leadCapture,
          ...partial,
        },
      },
      hasUnsavedChanges: true,
    }));
  },

  addStarterQuestion: (question) => {
    if (!question.trim()) return;
    const newQ: WidgetStarterQuestion = {
      id: `q_${Date.now()}`,
      question: question.trim(),
    };
    set((state) => ({
      config: {
        ...state.config,
        starterQuestions: [...state.config.starterQuestions, newQ],
      },
      hasUnsavedChanges: true,
    }));
  },

  removeStarterQuestion: (id) => {
    set((state) => ({
      config: {
        ...state.config,
        starterQuestions: state.config.starterQuestions.filter((q) => q.id !== id),
      },
      hasUnsavedChanges: true,
    }));
  },

  updateStarterQuestion: (id, text) => {
    set((state) => ({
      config: {
        ...state.config,
        starterQuestions: state.config.starterQuestions.map((q) =>
          q.id === id ? { ...q, question: text } : q
        ),
      },
      hasUnsavedChanges: true,
    }));
  },

  addWhitelistedDomain: (rawDomain) => {
    let domain = rawDomain.trim().toLowerCase();
    domain = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!domain) return;

    const { whitelistedDomains } = get().config;
    if (whitelistedDomains.includes(domain)) return;

    set((state) => ({
      config: {
        ...state.config,
        whitelistedDomains: [...state.config.whitelistedDomains, domain],
      },
      hasUnsavedChanges: true,
    }));
  },

  removeWhitelistedDomain: (domain) => {
    set((state) => ({
      config: {
        ...state.config,
        whitelistedDomains: state.config.whitelistedDomains.filter((d) => d !== domain),
      },
      hasUnsavedChanges: true,
    }));
  },

  setAssociatedKnowledgeBase: (associatedKnowledgeBase) => {
    set((state) => ({
      config: {
        ...state.config,
        associatedKnowledgeBase,
      },
      hasUnsavedChanges: true,
    }));
  },

  sendTestMessage: async (content) => {
    const trimmed = content.trim();
    if (!trimmed || get().isSimulatingResponse) return;

    const userMsg: WidgetChatMessage = {
      id: `usr_${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: 'Vừa xong',
    };

    set((state) => ({
      testMessages: [...state.testMessages, userMsg],
      isSimulatingResponse: true,
    }));

    // Simulate smart bot response based on enterprise RAG context
    await new Promise((r) => setTimeout(r, 600));

    let replyContent =
      'Cảm ơn câu hỏi của bạn! Theo dữ liệu từ kho tri thức doanh nghiệp, Sophia Platform thực thi trên Cloudflare Workers toàn cầu với thời gian khởi động lạnh P95 dưới 5ms, kết hợp Vectorize truy hồi ngữ nghĩa với ngưỡng tương đồng Cosine Similarity trên 0.65.';

    if (trimmed.toLowerCase().includes('kiến trúc') || trimmed.toLowerCase().includes('cloudflare')) {
      replyContent =
        'Kiến trúc Cloudflare Edge của Sophia Platform loại bỏ hoàn toàn máy chủ tập trung, chạy mã trên 330+ thành phố biên với chi phí Egress từ R2 bằng $0 và độ an toàn bất đối xứng SOC2.';
    } else if (trimmed.toLowerCase().includes('giá') || trimmed.toLowerCase().includes('billing')) {
      replyContent =
        'Hệ thống áp dụng sổ cái tín chỉ nguyên tử (Atomic Credit Ledger) với D1 db.batch(), bảo vệ số dư tức thì và tích hợp cổng thanh toán Stripe Checkout quốc tế.';
    }

    const botMsg: WidgetChatMessage = {
      id: `bot_${Date.now()}`,
      role: 'bot',
      content: replyContent,
      timestamp: 'Vừa xong',
    };

    set((state) => ({
      testMessages: [...state.testMessages, botMsg],
      isSimulatingResponse: false,
    }));
  },

  resetTestChat: () => {
    const greeting = get().config.appearance.greetingMessage;
    set({
      testMessages: [
        {
          id: 'msg_welcome',
          role: 'bot',
          content: greeting,
          timestamp: 'Vừa xong',
        },
      ],
      isSimulatingResponse: false,
    });
  },

  saveConfig: async () => {
    set({ isSaving: true });
    await new Promise((r) => setTimeout(r, 400));
    saveStoredConfig(get().config);
    set({
      isSaving: false,
      hasUnsavedChanges: false,
    });
  },

  resetConfigToDefault: () => {
    saveStoredConfig(DEFAULT_WIDGET_CONFIG);
    set({
      config: DEFAULT_WIDGET_CONFIG,
      hasUnsavedChanges: false,
      testMessages: INITIAL_TEST_MESSAGES,
    });
  },
}));
